// -----------------------------------------------------------------------------
// Fedari — central business source of truth.
// Approved facts only. Do NOT hardcode these values across components; import
// from here. Do NOT invent ratings, counts, addresses, certifications or slots.
// -----------------------------------------------------------------------------
import { CONTACT, waHref } from "@/lib/contact";

export const BUSINESS = {
  brand: "Fedari Inspection",
  legalName: "FEDARI BUILDING INSPECTION L.L.C",
  licence: "1647070",
  licensedActivity: "Building Inspection Services",
  phoneDisplay: CONTACT.phoneDisplay,
  phone: CONTACT.phone,
  whatsapp: CONTACT.whatsapp,
  email: CONTACT.email,
  primaryMarket: "Dubai",
  timezone: "Asia/Dubai",
  coreMessage: "Inspect Before You Accept.",
};

// Reporting promise: no absolute delivery time is published. The reporting
// timeframe is confirmed per booking in the accepted quotation; an eight-hour
// service may be confirmed in writing for eligible single-property bookings.
export const REPORT_PROMISE = "delivered within the confirmed timeframe";
export const REPORT_PROMISE_SHORT = "Report within the confirmed timeframe";
export const REPORT_PROMISE_MID = "within the confirmed timeframe";

// Approved CTA hierarchy.
export const CTA = {
  primary: "Get a Quote and Check Availability",
  primaryShort: "Get quote & availability",
  primaryNav: "Get a quote",
  primaryMobile: "Get quote",
  whatsapp: "WhatsApp Fedari",
};

// General scope statement — shown readably (not dominantly) on service pages.
export const SCOPE_STATEMENT =
  "Fedari provides a visual, non-destructive inspection of safe and accessible areas within the agreed scope. The inspection cannot guarantee the identification of every defect or concealed condition. Inaccessible areas and relevant limitations are noted in the report.";

// Property-type price guide — the ONLY source for public pricing.
export const PRICE_GUIDE = [
  { type: "Studio apartment", price: "From AED 799" },
  { type: "1-bedroom apartment", price: "From AED 999" },
  { type: "2-bedroom apartment", price: "From AED 1,299" },
  { type: "3-bedroom apartment", price: "From AED 1,599" },
  { type: "Townhouse", price: "From AED 1,999" },
  { type: "Villa", price: "From AED 2,499" },
  { type: "Reinspection", price: "From AED 399" },
  { type: "Penthouse, large villa & commercial", price: "Fixed quote after scope review" },
];

export const PRICING_NOTE =
  "Final pricing depends on property type, built-up area, location, access, external areas and the agreed inspection scope. Your exact fixed fee and inspection scope are confirmed before booking.";

// Trust-content truth model. A claim renders ONLY when its verified value and
// (where relevant) source are present. Default for unverified = hidden.
export const FLAGS = {
  verifiedReviews: true, // admin-approved client reviews served from the backend
  verifiedAverageRating: false, // no public aggregate review profile yet
  verifiedInspectionCount: false,
  verifiedCommunities: false,
  verifiedInspectors: false, // hide inspector name/photo/credentials until verified
  showEquipment: true, // standard visual-inspection tooling, used where scope requires
  promotionActive: false,
  promotionStart: null,
  promotionEnd: null,
  promotionTerms: null,
  googleReviewUrl: null,
};

// Build a WhatsApp deep link with an approved, prefilled message. Only REAL,
// provided values are appended — never literal placeholders.
export function waQuoteHref(fields = {}) {
  const { inspectionType = "", community = "", project = "", type = "", bedrooms = "", size = "", date = "" } = fields;
  let msg = "Hello Fedari, I would like an exact inspection quote and confirmed availability.";
  const parts = [];
  if (inspectionType) parts.push(`Inspection: ${inspectionType}`);
  if (community) parts.push(`Community: ${community}`);
  if (project) parts.push(`Project: ${project}`);
  if (type) parts.push(`Property type: ${type}`);
  if (bedrooms) parts.push(`Bedrooms: ${bedrooms}`);
  if (size) parts.push(`Approx size: ${size} sq ft`);
  if (date) parts.push(`Preferred date: ${date}`);
  if (parts.length) msg += " " + parts.join(". ") + ".";
  return `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(msg)}`;
}

// Default WhatsApp link (approved message, no placeholders).
export const waInspectorHref = waQuoteHref();

// Resolve the public price for a step-1 selection. Returns a price string from
// the approved table, or the safe fallback. Never presents an estimate as exact.
export function resolvePrice({ service = "", property_type = "", bedrooms = "" } = {}) {
  const s = service.toLowerCase();
  if (s.includes("re-inspection") || s.includes("de-snagging") || s.includes("reinspection")) return "From AED 399";
  const t = property_type.toLowerCase();
  if (t.includes("commercial") || t.includes("penthouse")) return "Fixed quote after scope review";
  if (t.includes("townhouse")) return "From AED 1,999";
  if (t.includes("villa")) return "From AED 2,499";
  if (t.includes("apartment")) {
    const b = String(bedrooms).toLowerCase();
    if (b === "studio") return "From AED 799";
    if (b === "1") return "From AED 999";
    if (b === "2") return "From AED 1,299";
    if (b === "3") return "From AED 1,599";
    return "Fixed quote after scope review";
  }
  return "Fixed quote after scope review";
}

export const BEDROOM_OPTIONS = ["Studio", "1", "2", "3", "4+"];

// Numeric lead value (AED) derived from resolvePrice — the single source of
// truth for public pricing. Used as the Google Ads conversion value so Smart
// Bidding optimises on real lead worth. Returns null when pricing needs a
// scope review (no honest value to send).
export function resolvePriceValue(fields = {}) {
  const m = resolvePrice(fields).match(/AED ([\d,]+)/);
  return m ? Number(m[1].replace(/,/g, "")) : null;
}

// Public Google review link (set REACT_APP_GOOGLE_REVIEW_URL to enable the
// "Leave a Google review" button). Hidden when empty.
export const GOOGLE_REVIEW_URL = (process.env.REACT_APP_GOOGLE_REVIEW_URL || "").trim();

export { waHref };
